import { localeRequestHeaders } from './localePreference';
import { apiErrorMessage } from './apiErrorMessages';

const API_PREFIX = '/api/v1';

async function apiRequest(path, user, options = {}) {
  const token = await user.getIdToken();
  let response;
  try {
    response = await fetch(`${API_PREFIX}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...localeRequestHeaders(),
        ...(options.headers || {}),
      },
    });
  } catch {
    throw new Error(apiErrorMessage('careerCoachNetwork'));
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || apiErrorMessage('careerCoachRequest'));
  }
  return response.json();
}

// Returns { summary: string, cached: boolean }.
// The backend caches the result in RTDB and only calls OpenAI when the
// scout document has been updated since the last generation.
export function getCareerFocus({ user }) {
  return apiRequest('/career-coach/focus', user, { method: 'POST' });
}

export function ensureCareerPaths({ user, mode, specificRole } = {}) {
  const payload = {};
  if (mode) payload.mode = mode;
  if (specificRole && specificRole.trim()) payload.specificRole = specificRole.trim();
  return apiRequest('/career-coach/career-paths', user, {
    method: 'POST',
    ...(Object.keys(payload).length > 0 ? { body: JSON.stringify(payload) } : {}),
  });
}

export function classifyCareerCoachIntent({ user, question }) {
  return apiRequest('/career-coach/ask/intent', user, {
    method: 'POST',
    body: JSON.stringify({ question }),
  });
}

export function startCvBuildEditSession({ user }) {
  return apiRequest('/career-coach/cv-build/start', user, {
    method: 'POST',
  });
}

export function turnCvBuildEditSession({ user, session, answer, choice }) {
  return apiRequest('/career-coach/cv-build/turn', user, {
    method: 'POST',
    body: JSON.stringify({ session, answer, choice }),
  });
}

export function previewCvBuildEditSession({ user, session }) {
  return apiRequest('/career-coach/cv-build/preview', user, {
    method: 'POST',
    body: JSON.stringify({ session }),
  });
}

export function generateCvBuildEditSession({ user, session }) {
  return apiRequest('/career-coach/cv-build/generate', user, {
    method: 'POST',
    body: JSON.stringify({ session }),
  });
}

export async function askCareerCoachStream({ user, question, history = [], onEvent }) {
  const token = await user.getIdToken();
  let response;
  try {
    response = await fetch(`${API_PREFIX}/career-coach/ask/stream`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...localeRequestHeaders(),
      },
      body: JSON.stringify({ question, history }),
    });
  } catch {
    throw new Error(apiErrorMessage('careerCoachNetwork'));
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    const failure = new Error(typeof error.detail === 'string' ? error.detail : error.detail?.message || apiErrorMessage('askRequest'));
    failure.code = error.code || error.detail?.code || (response.status === 402 ? 'monthly_limit_reached' : undefined);
    throw failure;
  }
  if (!response.body) {
    throw new Error(apiErrorMessage('askStreamingUnsupported'));
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      try {
        await onEvent(JSON.parse(trimmed));
      } catch {
        // Ignore malformed partial stream events.
      }
    }
  }

  buffer += decoder.decode();
  if (buffer.trim()) {
    try {
      await onEvent(JSON.parse(buffer.trim()));
    } catch {
      // Ignore malformed final stream events.
    }
  }
}
